/**
 * App-wide curtain transition — the same brand wipe the marketing site
 * (and this app's login) use, made available to any abrupt state change.
 *
 * Theme and language toggles both flip CSS at the root and reflow the
 * whole UI; swapping them behind an opaque slab hides the jump. Exposing
 * a single `runWithCurtain(apply)` keeps one Curtain mounted at the root
 * and lets each call site (the profile dropdown's theme + language items)
 * sequence its change through the shared phase machine — no per-toggle
 * duplication of the cover→swap→uncover rhythm.
 *
 * Honors `prefers-reduced-motion`: those users get the change applied
 * immediately with no slab.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  Curtain,
  CURTAIN_COVER_MS,
  CURTAIN_UNCOVER_MS,
  type CurtainPhase,
} from '@vollgas/shared/components'

interface CurtainTransitionValue {
  /** Run `apply` behind the curtain (or immediately under reduced-motion). */
  runWithCurtain: (apply: () => void) => void
  /** True while a wipe is in flight — call sites can ignore re-triggers. */
  isTransitioning: boolean
}

const CurtainTransitionContext = createContext<CurtainTransitionValue | null>(null)

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  if (typeof window.matchMedia !== 'function') return false
  return Boolean(window.matchMedia('(prefers-reduced-motion: reduce)')?.matches)
}

export function CurtainTransitionProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<CurtainPhase>('idle')
  // Cooldown lock — reject re-entrant triggers while a wipe is in flight so
  // a double-tap can't desync the phase machine.
  const lockRef = useRef(false)

  const runWithCurtain = useCallback((apply: () => void) => {
    if (lockRef.current) return
    if (prefersReducedMotion()) {
      apply()
      return
    }
    lockRef.current = true
    setPhase('covering')
    window.setTimeout(() => {
      // At peak coverage, apply the change behind the slab.
      apply()
      setPhase('covered')
      // Double rAF lets React commit the new theme/strings before the slab
      // retreats, so the new UI is fully painted as it's exposed.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          setPhase('uncovering')
          window.setTimeout(() => {
            setPhase('idle')
            lockRef.current = false
          }, CURTAIN_UNCOVER_MS + 30)
        })
      })
    }, CURTAIN_COVER_MS)
  }, [])

  const value = useMemo<CurtainTransitionValue>(
    () => ({ runWithCurtain, isTransitioning: phase !== 'idle' }),
    [runWithCurtain, phase],
  )

  return (
    <CurtainTransitionContext.Provider value={value}>
      {children}
      <Curtain phase={phase} leadClassName="bg-accent" trailClassName="bg-bg-elevated" />
    </CurtainTransitionContext.Provider>
  )
}

export function useCurtainTransition(): CurtainTransitionValue {
  const ctx = useContext(CurtainTransitionContext)
  if (!ctx) throw new Error('useCurtainTransition must be used within a CurtainTransitionProvider')
  return ctx
}
